// app/frontend/components/ui/Buttons/ThemedButton.tsx
import React from 'react';
import { Button, type ButtonProps } from '@mantine/core';
import '../../../styles/ThemedButton.css';

/**
 * ThemedButton accepts everything Mantine's `<Button>` accepts, plus native
 * button attributes (onClick / type / form) and the common polymorphic knobs
 * (`component`, `href`, `title`) so it can render as an Inertia Link or an
 * anchor without a type-cast at the call site.
 */
export type ThemedButtonProps = ButtonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonProps | 'color'> & {
    component?: React.ElementType;
    href?: string;
    target?: string;
    rel?: string;
  };

const ThemedButton = React.forwardRef<HTMLButtonElement, ThemedButtonProps>(
  ({ color = '#EA580C', children, className, ...props }, ref) => {
    // The house orange takes a themed page's accent instead (#2233, PageTheme.css).
    const accent = typeof color === 'string' && color.toLowerCase() === '#ea580c';
    return (
      <Button
        ref={ref}
        color={color}
        className={[accent ? 'themed-button--accent' : null, className].filter(Boolean).join(' ') || undefined}
        {...(props as ButtonProps)}
      >
        {children}
      </Button>
    );
  }
);

ThemedButton.displayName = 'ThemedButton';

export default ThemedButton;
